import React, { ReactElement, useEffect, useState } from 'react';
import './SubAccountShareList.less';
import { useDispatch, useSelector } from 'react-redux';
import { sharesActions, sharesSelectors } from '../../../../redux/shares';
import Empty from '../../../../components/envago/Empty/Empty';
import { jobsSelectors } from '../../../../redux/jobs';
import { pluralHelper } from '../../../../helper/TextHelper';
import Toolbar from '../../../../components/envago/Toolbar/Toolbar';
import Button from '../../../../components/envago/Button/Button';
import Dialog from '../../../../components/envago/Dialog/Dialog';
import { SharedAccountType } from '../../../../redux/shares/shares.types';
import AccountShareJobs from '../AccountShareJobs/AccountShareJobs';
import Icon from '../../../../components/envago/Icon/Icon';
import ShareJobDialog from '../../../common/ShareJobDialog/ShareJobDialog';
import { JobType } from '../../../../redux/jobs/jobs.types';
import { loginSelectors } from '../../../../redux/login';
import { AssignmentPresetType } from '../../../../redux/login/login.types';
import Loading from '../../../../components/common/Loading/Loading';
import ProgressBar from '../../../../components/envago/ProgressBar/ProgressBar';
import LabeledList from '../../../../components/envago/LabeledList/LabeledList';
import OptionalLabel from '../../../../components/envago/OptionalLabel/OptionalLabel';
import Card from '../../../../components/envago/Card/Card';
import BasicScreen from '../../../common/BasicScreen/BasicScreen';
import SubAccountDetails from '../SubAccountDetails/SubAccountDetails';
import { useParams } from 'react-router';
import Hint from '../../../../components/envago/Hint/Hint';
import ShareJobGroupsDialog from '../../../common/ShareJobGroupsDialog/ShareJobGroupsDialog';
import { useTranslation } from 'react-i18next';
import { useIsCompactLayout } from '../../../../helper/LayoutHelper';
import Information, { InformationType } from '../../../../components/envago/Information/Information';
import { applicationSelectors } from '../../../../redux/application';
import ExportWizardDialog from '../ExportWizardDialog/ExportWizardDialog';
import { ImportExcel } from '../../../common/ImportExcel/ImportExcel';
import { getShareSplitState, MAX_SHARE_JOBS } from '../../../../helper/ShareSplitState';
import { formatPresetRecipients } from '../../../../helper/PresetRecipients';

const ShareDetails = ({ share, accountId }: { share: SharedAccountType; accountId: string }) => {
    const { t } = useTranslation();

    const params: any = useParams();

    const subAccountIdParam = params?.subAccountId;

    const jobIds = share.jobs.map((j) => j.id);

    const jobsLoading = useSelector(jobsSelectors.isLoading);
    const jobs = useSelector(jobsSelectors.getJobsByIds(jobIds));
    const isSendSubaccountReminderMailtoLinkEnabled = useSelector(applicationSelectors.isSendSubaccountReminderMailtoLinkEnabled);

    const [detailsDialogVisible, setDetailsDialogVisible] = useState(subAccountIdParam === share.id);

    const jobsDone = jobs.filter((job) => job.done).length;

    return (
        <Card
            aria={{
                role: 'listitem',
            }}
        >
            <Toolbar
                title={share.name}
                toolbarButtons={[
                    isSendSubaccountReminderMailtoLinkEnabled && (
                        <Button
                            key={'reminder-mailto-button'}
                            aria={{
                                role: 'button',
                                'aria-label': 'Erinnerung per E-Mail senden',
                            }}
                            small={true}
                            iconButton={true}
                            ghost={true}
                            primary={false}
                            onClick={() => {
                                const subject = t('management.accounts.reminder.subject');

                                const loginURL = `${window.location.origin}/m/${share.token}`;
                                const body = t('management.accounts.reminder.body', {
                                    name: share.name,
                                    login: loginURL,
                                });

                                const mailToUrl = `mailto:?subject=${encodeURI(subject)}&body=${encodeURI(body)}`;

                                window.open(mailToUrl, '_self');
                            }}
                        >
                            <Icon path={Icon.Path.mdiBellRingOutline} />
                        </Button>
                    ),
                    <Button
                        aria={{
                            role: 'button',
                            'aria-label': 'Zugang anzeigen',
                        }}
                        small={true}
                        iconButton={true}
                        ghost={true}
                        primary={true}
                        key={'share-details-button'}
                        onClick={() => {
                            setDetailsDialogVisible(true);
                        }}
                    >
                        <Icon path={Icon.Path.mdiEye} />
                    </Button>,
                ]}
            />
            <LabeledList>
                <OptionalLabel label={t('common.total')}>{t('common.jobs', { count: jobs.length })}</OptionalLabel>
                <OptionalLabel label={t('common.progress')}>
                    <ProgressBar showPercentage={true} progress={jobsDone / jobs.length} />
                </OptionalLabel>
            </LabeledList>

            <Dialog
                visible={detailsDialogVisible}
                actions={[
                    <Button
                        onClick={() => {
                            setDetailsDialogVisible(false);
                        }}
                        key={'close-button'}
                        aria={{
                            'aria-label': 'Schließen',
                        }}
                        primary={true}
                    >
                        Schließen
                    </Button>,
                ]}
                closable={true}
                onClose={setDetailsDialogVisible}
                classNameContent={'sub-account-details-dialog'}
            >
                {jobsLoading && <Loading text={t('common.loadingJobs')} />}
                {!jobsLoading && <SubAccountDetails openedByToken={!!subAccountIdParam} accountId={accountId} subAccount={share} jobs={jobs} />}
            </Dialog>
        </Card>
    );
};

const SubAccountShareList = ({ accountId }: { accountId: string }) => {
    const groupShareActivated = false; // TODO: Group-Sharing is not yet supported -> add in later versions
    const isCompactLayout = useIsCompactLayout();

    const { t } = useTranslation();

    const [createShareVisible, setCreateShareVisible] = useState(false);
    const [selectedJobs, setSelectedJobs] = useState<JobType[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    const [selectedGroups, setSelectedGroups] = useState<{ name: string; data: JobType[] }[]>([]);
    const [appliedPreset, setAppliedPreset] = useState<AssignmentPresetType | undefined>(undefined);

    const [showShareDialog, setShowShareDialog] = useState(false);
    const [showShareGroupsDialog, setShowShareGroupsDialog] = useState(false);
    const [exportWizardVisible, setExportWizardVisible] = useState(false);
    const [importVisible, setImportVisible] = useState(false);

    const accountInfo = useSelector(loginSelectors.getAccountInfoForAccount(accountId));

    const shares = useSelector(sharesSelectors.getSharesForAccount(accountId));
    const showShareButton = useSelector(sharesSelectors.showShareButtonForAccount(accountId));
    const isExcelExportAllowed = useSelector(applicationSelectors.isExcelExportAllowed);
    const isExcelImportAllowed = useSelector(applicationSelectors.isExcelImportAllowed);
    const { sendSubaccountViaEmailDisabled } = useSelector(applicationSelectors.getFeaturesConfig);
    const quickSharing = useSelector(sharesSelectors.getQuickSharing);

    const { showSplit, presetEmails, baseDisabled, quickDisabled } = getShareSplitState({
        appliedPreset,
        sendSubaccountViaEmailDisabled,
        selectedCount: selectedJobs.length,
        quickSharing,
    });

    const recipients = formatPresetRecipients(presetEmails);
    const presetName = appliedPreset?.receiver?.name?.trim();
    const presetReshare = appliedPreset?.receiver?.reshare ?? false;
    const recipientText = recipients.isEmpty
        ? t('management.accounts.share.quickShareInfo.noEmails')
        : recipients.hiddenCount > 0
          ? `${recipients.visible.join(', ')} ${t('management.accounts.share.quickShareInfo.moreEmails', {
                count: recipients.hiddenCount,
            })}`
          : recipients.visible.join(', ');

    const dispatch = useDispatch();

    useEffect(() => {
        if (!showShareDialog) {
            dispatch(sharesActions.loadSubAccountsForAccount(accountId));
        }
    }, [showShareDialog]);

    return (
        <>
            <BasicScreen>
                <Toolbar
                    title={accountInfo?.name}
                    showBackButton={true}
                    toolbarButtons={
                        <>
                            {!isCompactLayout && isExcelExportAllowed && (
                                <Button small={true} ghost={true} key={'export-button'} onClick={() => setExportWizardVisible(true)}>
                                    <span className={'whitespace-nowrap'}>{t('management.excelExport.wizard.button')}</span>
                                </Button>
                            )}
                            {!isCompactLayout && isExcelImportAllowed && (
                                <Button small={true} ghost={true} key={'import-button'} onClick={() => setImportVisible(true)}>
                                    <span className={'whitespace-nowrap'}>{t('management.import.button')}</span>
                                </Button>
                            )}
                            {showShareButton && (
                                <Button
                                    className={'sub-account-share-list__share-btn'}
                                    small={true}
                                    primary={true}
                                    key={'share-button'}
                                    onClick={() => {
                                        setAppliedPreset(undefined);
                                        setCreateShareVisible(true);
                                    }}
                                >
                                    <Icon aria={{ 'aria-label': 'Teilen Icon' }} path={Icon.Path.mdiShare} />
                                    <span className={'whitespace-nowrap'}>
                                        {isCompactLayout
                                            ? t('management.accounts.subaccounts.buttons.shareShort')
                                            : t('management.accounts.subaccounts.buttons.share')}
                                    </span>
                                </Button>
                            )}
                        </>
                    }
                />
                <div role={'list'} className={'flex flex-col gap-2'}>
                    {shares?.map((share) => (
                        <ShareDetails key={share.id} accountId={accountId} share={share} />
                    ))}
                    {shares?.length === 0 && <Empty>{t('management.accounts.subaccounts.empty')}</Empty>}
                </div>
            </BasicScreen>

            <Dialog
                visible={createShareVisible}
                closable={true}
                title={t('management.accounts.share.title')}
                actions={[
                    groupShareActivated && (
                        <div className={'flex-1'}>
                            <Button
                                key="share-group"
                                accent={true}
                                block={true}
                                disabled={!(selectedGroups.length >= 1 && selectedJobs.length !== 1)}
                                onClick={() => {
                                    setCreateShareVisible(false);
                                    setShowShareGroupsDialog(true);
                                }}
                                aria={{
                                    role: 'button',
                                }}
                            >
                                {selectedGroups.length} {pluralHelper(selectedIds.length, 'Zugang', 'Zugänge')} erstellen
                            </Button>
                            <small>
                                {selectedGroups.length >= 1 && selectedJobs.length !== 1 ? (
                                    <span>
                                        Für jede ausgewählte Gruppe wird ein separater Zugang erstellt, insgesamt {selectedGroups.length}{' '}
                                        {pluralHelper(selectedIds.length, 'Zugang', 'Zugänge')}.
                                    </span>
                                ) : (
                                    <span>
                                        <Hint /> Nutze das Gruppierung-Symbol um mehrere Zugänge auf einmal zu erstellen.
                                    </span>
                                )}
                            </small>
                        </div>
                    ),
                    <div className={'flex-1'}>
                        {selectedJobs.length > MAX_SHARE_JOBS && (
                            <Information type={InformationType.ERROR} className={'text-sm text-red-500'}>
                                {t('management.accounts.share.error.maxjobs', {
                                    count: MAX_SHARE_JOBS,
                                })}
                            </Information>
                        )}
                        {showSplit && (
                            <div className={'text-sm text-gray-500 mb-2'}>
                                <div className={'mb-1'}>{t('management.accounts.share.quickShareInfo.intro')}</div>
                                <LabeledList>
                                    <OptionalLabel label={t('management.accounts.share.quickShareInfo.accessName')} className={'text-main'}>
                                        {presetName || t('management.accounts.share.quickShareInfo.nameFallback')}
                                    </OptionalLabel>
                                    <OptionalLabel label={t('management.accounts.share.quickShareInfo.reshareLabel')} className={'text-main'}>
                                        {presetReshare
                                            ? t('management.accounts.share.quickShareInfo.reshareYes')
                                            : t('management.accounts.share.quickShareInfo.reshareNo')}
                                    </OptionalLabel>
                                    <OptionalLabel label={t('management.accounts.share.quickShareInfo.emailsLabel')} className={'text-main'}>
                                        <span title={recipients.hiddenCount > 0 ? recipients.full : undefined}>{recipientText}</span>
                                    </OptionalLabel>
                                </LabeledList>
                            </div>
                        )}
                        <div className={showSplit ? 'flex gap-2' : ''}>
                            {showSplit && (
                                <div
                                    style={{ flex: 2 }}
                                    title={presetEmails.length === 0 ? t('management.accounts.share.buttons.quickShareNoEmails') : undefined}
                                >
                                    <Button
                                        key="quick-share"
                                        primary={true}
                                        block={true}
                                        isLoading={quickSharing}
                                        disabled={quickDisabled}
                                        onClick={() => {
                                            if (!appliedPreset) return;
                                            dispatch(
                                                sharesActions.quickShareWithPreset({
                                                    accountId,
                                                    jobIds: selectedIds,
                                                    name: appliedPreset.receiver.name,
                                                    reshare: appliedPreset.receiver.reshare,
                                                    emails: presetEmails,
                                                }),
                                            );
                                            setCreateShareVisible(false);
                                        }}
                                    >
                                        {t('management.accounts.share.buttons.quickShare', { count: selectedIds.length })}
                                    </Button>
                                </div>
                            )}
                            <div className={showSplit ? 'flex-1' : ''}>
                                <Button
                                    key="share"
                                    primary={!showSplit}
                                    ghost={showSplit}
                                    block={true}
                                    disabled={baseDisabled}
                                    onClick={() => {
                                        setCreateShareVisible(false);
                                        setShowShareDialog(true);
                                    }}
                                >
                                    <span className={'whitespace-normal'}>
                                        {showSplit
                                            ? t('management.accounts.share.buttons.shareStepByStep')
                                            : t('management.accounts.share.buttons.share', { count: selectedIds.length })}
                                    </span>
                                </Button>
                            </div>
                        </div>
                    </div>,
                ]
                    .filter(Boolean)
                    .map((a, index) => React.cloneElement(a as ReactElement, { key: index }))}
                onClose={setCreateShareVisible}
                size={'wide'}
            >
                <AccountShareJobs
                    enableAssignmentPresets
                    onPresetApplied={setAppliedPreset}
                    onSelectionChanged={(jobIds, jobs, groups) => {
                        setSelectedJobs(jobs);
                        setSelectedIds(jobIds);
                        setSelectedGroups(groups);
                    }}
                    accountId={accountId}
                />
            </Dialog>

            <ShareJobGroupsDialog groups={selectedGroups} visible={showShareGroupsDialog} onClose={setShowShareGroupsDialog} />

            <ShareJobDialog jobs={selectedJobs} preset={appliedPreset} visible={showShareDialog} onClose={setShowShareDialog} />

            <ImportExcel visible={importVisible} onClose={() => setImportVisible(false)} />

            <ExportWizardDialog visible={exportWizardVisible} onClose={() => setExportWizardVisible(false)} />
        </>
    );
};

export default SubAccountShareList;
